import styled from '@emotion/styled';
import { useCallback } from 'react';
import { BsArrowUpCircle, BsRocketTakeoff } from 'react-icons/bs';

import BayTooltip from '../../BayTooltip';
import { StyledNavButton } from '../styles/navStyles';
import type { TranslationFunction } from '../../types/common';
import type { BooleanCallback } from '../../types/callbacks';

interface LaunchpadButtonProps {
	t?: TranslationFunction;
	disabled?: boolean;
	platformType?: string;
	showLaunchpadModal?: boolean;
	setShowLaunchpadModal?: BooleanCallback;
	setIsDrawerOpen?: BooleanCallback;
}

const Launchpad = styled.div`
	display: flex;
	align-items: center;
	z-index: 102;
	position: relative;
	transition: all 0.2s ease-in-out;

	.iconText {
		color: var(--bs-black);
		margin-left: 0.5em;
		white-space: nowrap; /* Prevent text wrapping */
		display: none;
		width: 0px;
		opacity: 0;
		transform: translateX(-10px);
		transition:
			opacity 0.2s ease-in-out,
			transform 0.2s ease-in-out;
	}

	.launchpad-button {
		background-color: var(--bs-gray-100);
		z-index: 102;
		transition: all 0.2s ease-in-out;
		padding: 0.5rem 0.75rem;
		border-radius: 0.25rem;
		display: flex;
		align-items: center;
		min-width: 40px; /* Ensure minimum width for icon */

		&:hover {
			transition: all 0.2s ease-in-out;
			background-color: var(--bs-gray-200);
			width: auto;

			.iconText {
				display: inline;
				opacity: 1;
				width: 75px;
				transform: translateX(0);
			}
		}
	}

	/* Mobile styles */
	@media (max-width: 768px) {
		.launchpad-button {
			padding: 0.5rem;
		}
	}
`;

const UpgradeButton = styled.button`
	background-color: transparent;
	border: none;
	height: 24px;
	width: 24px;
	border-radius: 0.25rem;
	padding: 0.25rem;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		background-color: var(--bs-blue-100);
		color: var(--bs-blue);
	}
`;

const LaunchpadButton = ({
	t = () => '',
	disabled,
	platformType,
	showLaunchpadModal,
	setShowLaunchpadModal,
	setIsDrawerOpen,
}: LaunchpadButtonProps) => {
	const queryParams = new URLSearchParams(window.location.search);

	const handleClick = useCallback(() => {
		if (!showLaunchpadModal) {
			setShowLaunchpadModal?.(true);
			setIsDrawerOpen?.(true);
		} else {
			setShowLaunchpadModal?.(false);
			setIsDrawerOpen?.(false);
		}

		if (platformType !== 'social') {
			queryParams.set('page', `${queryParams.get('page')}#launchpad`);
		}
		window.location.hash = 'launchpad';
	}, [queryParams, platformType, showLaunchpadModal, setShowLaunchpadModal, setIsDrawerOpen]);

	return (
		<Launchpad className="ms-1 iconContainer">
			<StyledNavButton
				className={`launchpad-button me-2 me-md-0 ${disabled ? 'disabled opacity-75 cursor-not-allowed' : ''}`}
				disabled={disabled}
				role="button"
				onClick={handleClick}
			>
				<BsRocketTakeoff size={16} />
				<span className="iconText lh-1">{t('launchpad')}</span>
			</StyledNavButton>
			{disabled && (
				<BayTooltip overlay={t('upgrade')} placement="bottom">
					<UpgradeButton>
						<BsArrowUpCircle size={16} />
					</UpgradeButton>
				</BayTooltip>
			)}
		</Launchpad>
	);
};

export default LaunchpadButton;
